import { selectCustomers } from '../../../store/slices/customers';
import { SHOW_TASK_ASSIGNEE_ROLES } from '../taskBoardConfig';
import { useTaskTranslation } from '../hooks/useTaskTranslation';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Modal from '../../../components/UI/Modals/Modal';
import PrimaryButtonV2 from '../../../components/UI/Buttons/PrimaryButtonV2';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import {
  Task,
  fetchTaskById,
  fetchTaskHistory,
  resetTaskDetails,
  selectTask,
  selectTasks,
  selectTaskStatus,
  selectTaskHistory,
  selectTaskHistoryStatus,
  TASK_STATUSES,
  TASK_PRIORITIES,
  TaskPriority,
  TaskUpdatePayloadRequest,
  TaskStatus,
} from '../../../store/slices/tasks';
import {
  findTaskCustomer,
  taskAssigneeColor,
  taskAssigneeDesignation,
  taskDesignationLabel,
  taskInitials,
  taskDate,
  taskStatusLabel,
  taskDateInput,
  taskStatusKey,
} from '../taskUtils';
import classes from './EditTaskModal.module.css';
import { useTaskCustomerSelection } from '../hooks/useTaskCustomerSelection';
import { TaskCustomerPicker } from './TaskCustomerPicker';
import { useTaskAssigneeSelection } from '../hooks/useTaskAssigneeSelection';
import { TaskAssigneePicker } from './TaskAssigneePicker';
import type { TaskSocketActions } from '../../../context/TaskContext';
import { TaskLoadingIndicator } from './TaskLoadingIndicator';
import taskTypeIcon from '../../../assets/icons/taskBoardIcon.svg';

const taskDraft = (task: Task) => ({
  title: task.title || '',
  description: task.description || '',
  priority: task.priority || '',
  status: TASK_STATUSES.find((value) => taskStatusKey(value) === taskStatusKey(task.status)) || '',
  startedAt: taskDateInput(task.datetime_start),
  finishedAt: taskDateInput(task.datetime_finish),
});

export const EditTaskModal = ({
  task: summary,
  onClose,
  taskSocket,
}: {
  task: Task;
  onClose: () => void;
  taskSocket: TaskSocketActions;
}) => {
  const { translations, taskText, currentLang } = useTaskTranslation();
  const dispatch = useAppDispatch();
  const customerSelection = useTaskCustomerSelection();
  const assigneeSelection = useTaskAssigneeSelection();
  const detail = useAppSelector(selectTask);
  const customers = useAppSelector(selectCustomers);
  const latestSummary =
    useAppSelector(selectTasks).find((task) => task.task_id === summary.task_id) || summary;
  const detailStatus = useAppSelector(selectTaskStatus);
  const history = useAppSelector(selectTaskHistory);
  const historyStatus = useAppSelector(selectTaskHistoryStatus);
  const active = useRef(true);
  const requests = useRef<{ abort: () => void }[]>([]);
  const taskId = summary.task_id;
  const task =
    detail && detail.task_id === taskId && detailStatus === 'fulfilled' ? detail : latestSummary;
  const customer = findTaskCustomer(task.customer_id, customers);
  const customerName = [customer?.person_firstname, customer?.person_lastname]
    .filter(Boolean)
    .join(' ')
    .trim();
  const designation = taskAssigneeDesignation(
    task,
    assigneeSelection.options.map(({ person }) => person)
  );
  const [draft, setDraft] = useState(() => taskDraft(summary));
  const [baseline, setBaseline] = useState(() => taskDraft(summary));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  // Comments are temporarily hidden.
  /*
  const [comment, setComment] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [commentError, setCommentError] = useState('');
  const [commentPosted, setCommentPosted] = useState(false);
  const commentSubmitting = useRef(false);
  */
  const edited = useRef(false);
  const submitting = useRef(false);
  const busy = saving /* || postingComment */ || taskSocket.pendingTaskIds.length > 0;
  const disabled = busy || !taskSocket.connected || taskId == null || detailStatus === 'pending';
  const hasChanges =
    customerSelection.hasSelection ||
    assigneeSelection.hasSelection ||
    (Object.keys(draft) as (keyof typeof draft)[]).some((key) => draft[key] !== baseline[key]);

  useEffect(() => {
    if (!edited.current) {
      setDraft(taskDraft(task));
      setBaseline(taskDraft(task));
    }
  }, [task]);

  const changeField = (field: keyof typeof draft, value: string) => {
    edited.current = true;
    setSaveError('');
    setDraft((previous) => ({ ...previous, [field]: value }));
  };

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();
    if (disabled || submitting.current || taskId == null || !hasChanges) return;
    setSaveError('');
    if (!draft.title.trim()) {
      setSaveError(translations('task_board.enter_a_task_title'));
      return;
    }
    const changes: {
      payload: TaskUpdatePayloadRequest;
      field: keyof typeof draft | 'customer';
    }[] = [];
    if (draft.title.trim() !== baseline.title)
      changes.push({ field: 'title', payload: { taskId, title: draft.title.trim() } });
    if (draft.description !== baseline.description)
      changes.push({ field: 'description', payload: { taskId, description: draft.description } });
    if (draft.status && draft.status !== baseline.status)
      changes.push({ field: 'status', payload: { taskId, status: draft.status as TaskStatus } });
    if (draft.priority && draft.priority !== baseline.priority)
      changes.push({
        field: 'priority',
        payload: { taskId, priority: draft.priority as TaskPriority },
      });
    for (const field of ['startedAt', 'finishedAt'] as const) {
      if (draft[field] === baseline[field]) continue;
      if (!draft[field] || Number.isNaN(new Date(draft[field]).getTime())) {
        setSaveError(
          translations(
            'task_board.enter_a_valid_date_and_time_the_task_service_does_not_support_clearing_saved_dates'
          )
        );
        return;
      }
      const value = new Date(draft[field]).toISOString();
      const payload: TaskUpdatePayloadRequest =
        field === 'startedAt'
          ? { taskId, datetimeStart: value }
          : { taskId, datetimeFinish: value };
      changes.push({ field, payload });
    }
    submitting.current = true;
    setSaving(true);
    try {
      const assignee = assigneeSelection.hasSelection
        ? assigneeSelection.getAssigneePayload()
        : null;
      const customer = await customerSelection.getCustomerPayload();
      if (!active.current) return;
      if (customer) changes.push({ field: 'customer', payload: { taskId, customer } });
      // The service accepts exactly one changed field per task:update event.
      for (const change of changes) {
        await taskSocket.updateTask(change.payload);
        if (!active.current) return;
        if (change.field === 'customer') {
          customerSelection.clearSelection();
          customerSelection.setSearch('');
        } else {
          const field = change.field;
          setBaseline((previous) => ({ ...previous, [field]: draft[field] }));
        }
      }
      // Apply assignment last, after all edits that use the current permissions.
      if (assignee) {
        await taskSocket.assignTask({ taskId, person: assignee });
        if (!active.current) return;
        assigneeSelection.clearSelection();
        assigneeSelection.setSearch('');
      }
      const request = dispatch(fetchTaskById(taskId));
      requests.current.push(request);
      const result = await request;
      if (!active.current) return;
      if (fetchTaskById.fulfilled.match(result)) {
        setDraft(taskDraft(result.payload));
        setBaseline(taskDraft(result.payload));
        edited.current = false;
      }
      requests.current.push(dispatch(fetchTaskHistory(taskId)));
      toast.success(translations('task_board.task_updated'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } catch (error) {
      if (active.current)
        setSaveError(
          error instanceof Error
            ? error.message
            : translations('task_board.unable_to_save_the_task')
        );
    } finally {
      submitting.current = false;
      if (active.current) setSaving(false);
    }
  };

  /*
  const handlePostComment = async () => {
    if (disabled || commentSubmitting.current || taskId == null || !comment.trim()) return;
    commentSubmitting.current = true;
    setPostingComment(true);
    setCommentError('');
    setCommentPosted(false);
    try {
      await taskSocket.createComment({ taskId, text: comment.trim() });
      if (!active.current) return;
      setComment('');
      setCommentPosted(true);
      requests.current.push(dispatch(fetchTaskHistory(taskId)));
    } catch (error) {
      if (active.current)
        setCommentError(error instanceof Error ? error.message : translations('task_board.unable_to_post_the_comment'));
    } finally {
      commentSubmitting.current = false;
      if (active.current) setPostingComment(false);
    }
  };

  */

  useEffect(() => {
    active.current = true;
    dispatch(resetTaskDetails());
    if (taskId != null) {
      requests.current = [dispatch(fetchTaskById(taskId)), dispatch(fetchTaskHistory(taskId))];
    }
    return () => {
      active.current = false;
      requests.current.forEach((request) => request.abort());
      requests.current = [];
      dispatch(resetTaskDetails());
    };
  }, [dispatch, taskId]);

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!submitting.current /* && !commentSubmitting.current */) onClose();
      }}
      hideCloseButton={saving /* || postingComment */}
      containerPadding="0"
      width="80%"
      maxWidth="960px"
      containerClassName={classes.modalContainer}
      headerClassName={classes.modalHeader}
      header={
        <div className={classes.headerTitleWrapper}>
          <div className={classes.headerText}>
            <div
              className={classes.mainTitle}
              title={task.title || translations('task_board.untitled_task')}
            >
              {task.title || translations('task_board.untitled_task')}
            </div>
            <div className={classes.subtitleCode}>
              {taskId != null ? `ECO-${taskId}` : translations('task_board.no_task_id')}
            </div>
          </div>
        </div>
      }
      divider
      headerStyles={{ padding: '20px', width: '100%' }}
      closeButtonStyles={{ width: 20, height: 20, flexShrink: 0 }}
    >
      <div className={classes.loadingContainer} aria-busy={detailStatus === 'pending'}>
        {detailStatus === 'pending' && (
          <div className={classes.loadingOverlay}>
            <TaskLoadingIndicator />
          </div>
        )}
        <form
          className={classes.container}
          onSubmit={handleSave}
          aria-label={translations('task_board.edit_task')}
        >
          <div className={classes.topMetaRow}>
            <div className={classes.assignedBlock}>
              <div className={classes.sectionLabel}>{translations('task_board.assigned_to')}</div>
              <div className={classes.personRow}>
                {task.person_name_assign?.trim() && (
                  <div
                    className={classes.avatarCircle}
                    style={taskAssigneeColor(task.person_id_assign, task.person_name_assign)}
                  >
                    {taskInitials(task.person_name_assign)}
                  </div>
                )}
                <div className={classes.personText}>
                  {task.person_name_assign || translations('task_board.unassigned')}
                  {SHOW_TASK_ASSIGNEE_ROLES && designation && (
                    <span className={classes.personDesignation}>
                      {taskDesignationLabel(designation, translations)}
                    </span>
                  )}
                </div>
              </div>
              <div className={classes.sectionLabel}>{translations('task_board.customer')}</div>
              <div className={classes.personRow}>
                {(task.customer_id != null || customerName) && (
                  <div
                    className={classes.avatarCircle}
                    style={taskAssigneeColor(task.customer_id, customerName || undefined)}
                    aria-hidden="true"
                  >
                    {taskInitials(customerName || undefined)}
                  </div>
                )}
                <div className={classes.personText}>
                  {customerName ||
                    (task.customer_id != null
                      ? taskText('task_board.customer_id', { id: task.customer_id })
                      : translations('task_board.unassigned'))}
                  {customerName && task.customer_id != null && (
                    <span className={classes.customerId}>
                      {taskText('task_board.customer_id', { id: task.customer_id })}
                    </span>
                  )}
                </div>
              </div>
              <div className={classes.typePriorityRow}>
                <div className={classes.metaField}>
                  <div className={classes.sectionLabel}>{translations('task_board.type')}</div>
                  <div className={classes.typeBadge}>
                    <img src={taskTypeIcon} alt="" aria-hidden="true" />
                    <span>{task.type || translations('task_board.no_type')}</span>
                  </div>
                </div>
                <div className={classes.metaField}>
                  <label className={classes.fieldLabel} htmlFor="task-priority">
                    {translations('task_board.priority')}
                  </label>
                  <select
                    id="task-priority"
                    className={classes.prioritySelect}
                    value={draft.priority}
                    disabled={disabled}
                    onChange={(event) => changeField('priority', event.target.value)}
                  >
                    {!draft.priority && (
                      <option value="">{translations('task_board.unspecified')}</option>
                    )}
                    {TASK_PRIORITIES.map((value) => (
                      <option key={value} value={value}>
                        {translations(`task_board.priority_${value}`)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className={classes.metaBlock}>
              <div className={classes.metaField}>
                <label className={classes.fieldLabel} htmlFor="task-status">
                  {translations('task_board.status')}
                </label>
                <select
                  id="task-status"
                  className={classes.statusSelect}
                  value={draft.status}
                  disabled={disabled}
                  onChange={(event) => changeField('status', event.target.value)}
                >
                  {!draft.status && (
                    <option value="">{taskStatusLabel(task.status, translations)}</option>
                  )}
                  {TASK_STATUSES.map((value) => (
                    <option key={value} value={value}>
                      {taskStatusLabel(value, translations)}
                    </option>
                  ))}
                </select>
              </div>
              <div className={classes.metaRow}>
                <div className={classes.metaField}>
                  <label className={classes.fieldLabel} htmlFor="task-start">
                    {translations('task_board.start_date_and_time')}
                  </label>
                  <input
                    id="task-start"
                    type="datetime-local"
                    className={classes.dateInput}
                    value={draft.startedAt}
                    disabled={disabled}
                    onChange={(event) => changeField('startedAt', event.target.value)}
                  />
                </div>
                <div className={classes.metaField}>
                  <label className={classes.fieldLabel} htmlFor="task-finish">
                    {translations('task_board.end_date_and_time')}
                  </label>
                  <input
                    id="task-finish"
                    type="datetime-local"
                    className={classes.dateInput}
                    value={draft.finishedAt}
                    disabled={disabled}
                    onChange={(event) => changeField('finishedAt', event.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={classes.section}>
            <label className={classes.sectionLabel} htmlFor="task-title">
              {translations('task_board.title')}
            </label>
            <input
              id="task-title"
              className={classes.titleInput}
              value={draft.title}
              required
              disabled={disabled}
              onChange={(event) => changeField('title', event.target.value)}
            />
          </div>
          <div className={classes.section}>
            <label className={classes.sectionLabel} htmlFor="task-description">
              {translations('task_board.description')}
            </label>
            <textarea
              id="task-description"
              className={classes.descriptionArea}
              value={draft.description}
              disabled={disabled}
              placeholder={translations('task_board.add_a_description')}
              onChange={(event) => changeField('description', event.target.value)}
            />
          </div>
          <TaskAssigneePicker
            selection={assigneeSelection}
            disabled={disabled}
            clearLabel={translations('task_board.cancel_assignment_change')}
            onSelectionChange={() => setSaveError('')}
          />
          <TaskCustomerPicker
            selection={customerSelection}
            disabled={disabled}
            clearLabel={translations('task_board.cancel_customer_change')}
            onSelectionChange={() => setSaveError('')}
          />
          <div className={classes.section}>
            <div className={classes.sectionLabel}>{translations('task_board.task_history')}</div>
            <div
              className={classes.historyBox}
              role="region"
              aria-label={translations('task_board.task_history')}
              tabIndex={0}
            >
              {historyStatus === 'pending' && !history.length ? (
                <TaskLoadingIndicator />
              ) : history.length ? (
                <ul className={classes.historyList}>
                  {history.map((entry, index) => (
                    <li key={entry.history_id ?? index}>
                      <div>
                        {entry.log_description ||
                          entry.history_type ||
                          translations('task_board.task_event')}
                      </div>
                      <small>
                        {entry.person_name || translations('task_board.unknown')} •{' '}
                        {taskDate(entry.datetime, false, currentLang)}{' '}
                        {taskDate(entry.datetime, true, currentLang)}
                      </small>
                    </li>
                  ))}
                </ul>
              ) : historyStatus === 'fulfilled' ? (
                <div>{translations('task_board.no_history_available')}</div>
              ) : null}
            </div>
          </div>
          {/* Comments are temporarily hidden.
          <div className={classes.section}>
            <label className={classes.sectionLabel} htmlFor="task-comment">
              {translations('task_board.leave_a_comment')}
            </label>
            <textarea
              id="task-comment"
              className={classes.commentArea}
              rows={3}
              value={comment}
              disabled={disabled}
              placeholder={translations('task_board.write_a_comment')}
              aria-describedby={commentError ? 'task-comment-error' : undefined}
              onChange={(event) => {
                setComment(event.target.value);
                setCommentError('');
                setCommentPosted(false);
              }}
            />
            {commentError && (
              <div id="task-comment-error" role="alert" className={classes.saveError}>
                {commentError}
              </div>
            )}
            {commentPosted && <div role="status" className={classes.commentSuccess}>{translations('task_board.comment_posted')}</div>}
            <div className={classes.commentActions}>
              <PrimaryButtonV2
                type="button"
                text={postingComment ? translations('task_board.posting') : translations('task_board.post_comment')}
                disabled={disabled || !comment.trim()}
                onClick={handlePostComment}
                buttonStyle={{ minWidth: 140, height: 40 }}
              />
            </div>
          </div>
          */}
          <div className={classes.footerMeta}>
            {taskText('task_board.created_on_date_time_by_name', {
              date: taskDate(task.datetime_create, false, currentLang),
              time: taskDate(task.datetime_create, true, currentLang),
              name: task.person_name_create || translations('task_board.unknown'),
            })}
          </div>
          <div className={classes.footerButtons}>
            {saveError && (
              <div role="alert" className={classes.saveError}>
                {saveError}
              </div>
            )}
            {!taskSocket.connected && (
              <div role="status" className={classes.connectionStatus}>
                {translations('task_board.waiting_for_the_task_connection')}
              </div>
            )}
            <PrimaryButtonV2
              invertedColors
              text={translations('task_board.close')}
              type="button"
              disabled={saving /* || postingComment */}
              onClick={onClose}
              buttonStyle={{ minWidth: 120, height: 40 }}
            />
            <PrimaryButtonV2
              type="submit"
              text={
                saving ? translations('task_board.saving') : translations('task_board.save_changes')
              }
              disabled={disabled || !hasChanges}
              onClick={() => { }}
              buttonStyle={{ minWidth: 140, height: 40 }}
            />
          </div>
        </form>
      </div>
    </Modal>
  );
};
